import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, CartesianGrid, Cell, ReferenceLine } from "recharts";
import { Factory } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 }));
const pct = (n) => (n == null ? "—" : `${n}%`);
const TREND = { high: ["#34D399", "Above avg"], avg: ["#FBBF24", "Near avg (±10%)"], low: ["#F87171", "Below avg"] };
const selectCls = "bg-[#0A0B10] border border-border px-3 py-1.5 text-[11px] uppercase tracking-wider text-white cursor-pointer focus:outline-none focus:border-[#FBBF24] transition-colors";

export const PmProductionSummaryCard = ({ showMexico, preferredJob }) => {
  const [job, setJob] = useState("");
  const [year, setYear] = useState(null);
  const [plant, setPlant] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (preferredJob) setJob(preferredJob); }, [preferredJob]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { show_mexico: showMexico };
    if (job) params.job = job;
    if (year) params.year = year;
    if (plant) params.plant = plant;
    api.get("/pm-view/production-summary", { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [job, year, plant, showMexico]);

  return (
    <section className="relative bg-[#12141D] border border-border p-5" data-testid="pm-production-summary-card">
      <span className="absolute top-0 left-0 right-0 h-[2px] bg-[#FBBF24]" />
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
            <Factory size={14} className="text-[#FBBF24]" /> Production Summary
          </div>
          <h3 className="font-heading font-bold text-xl mt-1" data-testid="pm-prod-job-title">{data?.job || "—"}</h3>
          <p className="text-xs text-muted-foreground mt-1">Monthly produced SF (INV + SHIP, by Cast Date) vs. budget estimated SF</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="pm-prod-plant-select" value={data?.plant || ""} onChange={(e) => setPlant(e.target.value)} className={`${selectCls} max-w-[200px]`}>
            <option value="">All Plants</option>
            {(data?.plants || []).map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select data-testid="pm-prod-job-select" value={data?.job || ""} onChange={(e) => setJob(e.target.value)} className={`${selectCls} max-w-[200px]`}>
            {(data?.jobs || []).map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
          <select data-testid="pm-prod-year-select" value={data?.year || ""} onChange={(e) => setYear(Number(e.target.value))} className={selectCls}>
            {(data?.years?.length ? data.years : [data?.year]).filter(Boolean).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      <SummaryTiles data={data} />
      <MonthlyChart data={data} loading={loading} />
    </section>
  );
};

const SummaryTiles = ({ data }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
    {[
      ["Total Project SF", fmt(data?.total_project_sf), "pm-prod-total-sf"],
      [`SF in ${data?.year ?? ""}`, fmt(data?.year_sf), "pm-prod-year-sf"],
      [`% of Project in ${data?.year ?? ""}`, pct(data?.year_pct), "pm-prod-year-pct"],
      ["Produced to Date", `${fmt(data?.produced_to_date_sf)} · ${pct(data?.to_date_pct)}`, "pm-prod-to-date"],
    ].map(([label, value, tid]) => (
      <div key={tid} className="bg-[#0A0B10] border border-border p-3" data-testid={tid}>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="font-mono font-semibold text-lg tabular-nums mt-1">{value}</div>
      </div>
    ))}
  </div>
);

const MonthlyChart = ({ data, loading }) => {
  if (loading && !data) return <div className="py-16 text-center text-sm uppercase tracking-widest text-muted-foreground">Loading…</div>;
  const months = data?.months || [];
  return (
    <div data-testid="pm-prod-chart">
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height={260} minWidth={200}>
          <BarChart data={months} margin={{ top: 24, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#1F2230" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} width={50} />
            <Tooltip cursor={{ fill: "#ffffff08" }} contentStyle={{ background: "#0A0B10", border: "1px solid #2A2D3A", fontSize: 12 }}
              formatter={(v, _n, p) => [`${fmt(v)} SF · ${pct(p.payload.pct)}`, "Produced"]} />
            {data?.monthly_avg_sf > 0 && (
              <ReferenceLine y={data.monthly_avg_sf} stroke="#94A3B8" strokeDasharray="4 4"
                label={{ value: `Avg ${fmt(data.monthly_avg_sf)}`, fill: "#94A3B8", fontSize: 10, position: "insideTopRight" }} />
            )}
            <Bar dataKey="sf" fill="#FBBF24" radius={[2, 2, 0, 0]}>
              {months.map((m) => <Cell key={m.month} fill={(TREND[m.trend] || TREND.avg)[0]} />)}
              <LabelList dataKey="sf" position="top" formatter={(v) => (v ? fmt(v) : "")} fill="#E2E8F0" fontSize={10} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-12 gap-1 mt-2 pl-[50px]" data-testid="pm-prod-pct-row">
        {months.map((m) => (
          <div key={m.month} className="text-center font-mono text-[11px] tabular-nums" style={{ color: (TREND[m.trend] || TREND.avg)[0] }} data-testid={`pm-prod-pct-${m.month}`}>
            {pct(m.pct)}
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-1 pl-[50px]">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">% of total project SF</div>
        <div className="flex items-center gap-4" data-testid="pm-prod-legend">
          {Object.entries(TREND).map(([k, [c, label]]) => (
            <span key={k} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground">
              <span className="h-2 w-2" style={{ background: c }} /> {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
